const chapter = {
  id: 'photosynthesis-higher-plants',
  slug: 'photosynthesis-higher-plants',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Photosynthesis in Higher Plants',
        hindi: 'उच्च पादपों में प्रकाश संश्लेषण',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Plant Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'early-experiments',
      title: {
        english: 'Early Experiments',
        hindi: 'प्रारम्भिक प्रयोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Joseph Priestley (1770)** — discovered essential role of air (mint plant + candle).',
        hindi: '**जोसेफ प्रीस्टले (1770)** - ने हवा की आवश्यक भूमिका (पुदीने का पौधा + मोमबत्ती) की खोज की।',
            },
            {
              english: '**Jan Ingenhousz (1779)** — sunlight is essential; only green parts release O₂.',
        hindi: '**जन इंगेनहौज़ (1779)** - सूर्य का प्रकाश आवश्यक है; केवल हरे भाग O₂ छोड़ते हैं।',
            },
            {
              english: '**Julius von Sachs (1854)** — green parts produce **glucose** (stored as starch).',
        hindi: '**जूलियस वॉन सैक्स (1854)** - हरे भाग **ग्लूकोज** (स्टार्च के रूप में संग्रहीत) का उत्पादन करते हैं।',
            },
            {
              english: '**T.W. Engelmann (1888)** — first **action spectrum** of photosynthesis using *Cladophora* + prism + aerobic bacteria; max activity in **blue & red**.',
        hindi: '**टी.डब्ल्यू. एंगेलमैन (1888)** - *क्लैडोफोरा* + प्रिज्म + एरोबिक बैक्टीरिया का उपयोग करके प्रकाश संश्लेषण का पहला **एक्शन स्पेक्ट्रम**; **नीले और लाल** में अधिकतम गतिविधि।',
            },
            {
              english: '**Cornelis van Niel** — purple/green sulphur bacteria use H₂S (no O₂); generalised: $2 H_2 A + CO_2 \\rightarrow 2 A + CH_2 O + H_2 O$. **Proved O₂ in photosynthesis comes from H₂O**, not CO₂.',
        hindi: '**कॉर्नेलिस वैन नील** — purple/green सल्फर बैक्टीरिया H₂S (कोई O₂ नहीं) का उपयोग करते हैं; सामान्यीकृत: $2 H_2 A + CO_2 \\rightarrow 2 A + CH_2 O + H_2 O$. **सिद्ध है कि प्रकाश संश्लेषण में O₂ H₂O** से आता है, CO₂ से नहीं।',
            },
            {
              english: 'Confirmed by **Ruben & Kamen** using ¹⁸O-labelled water.',
        hindi: '**रूबेन और कामेन** द्वारा ¹⁸O-लेबल वाले पानी का उपयोग करके पुष्टि की गई।',
            },
            {
              english: 'Overall equation:',
        hindi: 'समग्र समीकरण:',
            },
          ],
        },
        {
          type: 'equation',
          latex: '6\\,CO_2 + 12\\,H_2O \\xrightarrow[\\text{chlorophyll}]{\\text{light}} C_6H_{12}O_6 + 6\\,O_2 + 6\\,H_2O',
        },
      ],
    },
    {
      id: 'chloroplast-pigments',
      title: {
        english: 'Chloroplast & Pigments',
        hindi: 'हरित लवक और वर्णक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Chloroplast (semi-autonomous, double-membrane) — stroma + grana (stacks of thylakoids) + intergranal lamellae.',
        hindi: 'क्लोरोप्लास्ट (अर्ध-स्वायत्त, डबल-झिल्ली) - स्ट्रोमा + ग्रैना (थायलाकोइड्स के ढेर) + इंटरग्रेनल लैमेला। * **थायलाकोइड झिल्ली** पर हल्की प्रतिक्रियाएँ; **स्ट्रोमा** में डार्क (बायोसिंथेटिक)।',
            },
            {
              english: 'Light reactions on **thylakoid membrane**; dark (biosynthetic) in **stroma**.',
        hindi: '**थायलाकोइड झिल्ली** पर हल्की प्रतिक्रियाएँ; **स्ट्रोमा** में डार्क (बायोसिंथेटिक)।',
            },
            {
              english: 'Pigments (separated by paper chromatography by Stahl):',
        hindi: 'रंगद्रव्य (स्टाहल द्वारा पेपर क्रोमैटोग्राफी द्वारा अलग किए गए):',
            },
            {
              english: '• **Chlorophyll a** (bright/blue-green) — chief pigment; absorbs **430 (blue) & 662 (red) nm**.',
        hindi: '• **क्लोरोफिल ए** (bright/blue-हरा) - मुख्य वर्णक; **430 (नीला) और 662 (लाल) एनएम** को अवशोषित करता है।',
            },
            {
              english: '• **Chlorophyll b** (yellow-green), **xanthophylls** (yellow), **carotenoids** (yellow-orange) — accessory pigments; widen absorption + protect from photo-oxidation.',
        hindi: '• **क्लोरोफिल बी** (पीला-हरा), **ज़ैन्थोफिल्स** (पीला), **कैरोटेनॉयड्स** (पीला-नारंगी) - सहायक रंगद्रव्य; अवशोषण को बढ़ाएं + फोटो-ऑक्सीकरण से बचाएं। **एक्शन स्पेक्ट्रम** ≈ **क्लोरोफिल ए का अवशोषण स्पेक्ट्रम** → पुष्टि करता है कि सीएचएल ए प्राथमिक वर्णक है।',
            },
            {
              english: '**Action spectrum** ≈ **absorption spectrum of chlorophyll a** → confirms chl a is primary pigment.',
        hindi: '**एक्शन स्पेक्ट्रम** ≈ **क्लोरोफिल ए का अवशोषण स्पेक्ट्रम** → पुष्टि करता है कि सीएचएल ए प्राथमिक वर्णक है।',
            },
            {
              english: 'Maximum photosynthesis in **blue & red light**; minimum in green (reflected → leaves green).',
        hindi: '**नीली और लाल रोशनी** में अधिकतम प्रकाश संश्लेषण; हरे रंग में न्यूनतम (प्रतिबिंबित → पत्तियां हरी)।',
            },
          ],
        },
      ],
    },
    {
      id: 'light-reaction',
      title: {
        english: 'Light Reaction (Photochemical Phase)',
        hindi: 'प्रकाश अभिक्रिया (प्रकाशरासायनिक चरण)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Two photosystems — pigment + electron transport organisations on thylakoid:',
        hindi: 'दो फोटो सिस्टम - थायलाकोइड पर वर्णक + इलेक्ट्रॉन परिवहन संगठन:',
            },
            {
              english: '• **PS I** — reaction-centre chl a absorbs **700 nm (P700)**.',
        hindi: '• **पीएस I** - प्रतिक्रिया-केंद्र सीएचएल ए अवशोषित **700 एनएम (पी700)**।',
            },
            {
              english: '• **PS II** — reaction-centre chl a absorbs **680 nm (P680)**.',
        hindi: '• **पीएस II** - प्रतिक्रिया-केंद्र सीएचएल **680 एनएम (पी680)** को अवशोषित करता है। [[[पीपीएक्स_एसईजी_4]] **जेड-स्कीम (गैर-चक्रीय फोटोफॉस्फोराइलेशन)** - पीएस II → पीक्यू → साइट बी₆/एफ → पीसी → पीएस I → एफडी → **एनएडीपी⁺ रिडक्टेस** → एनएडीपीएच।',
            },
            {
              english: '**Z-scheme (non-cyclic photophosphorylation)** — PS II → PQ → Cyt b₆/f → PC → PS I → Fd → **NADP⁺ reductase** → NADPH.',
        hindi: '**Z-योजना (गैर-चक्रीय फोटोफॉस्फोराइलेशन)** - PS II → PQ → Cyt b₆/f → PC → PS I → Fd → **NADP⁺ रिडक्टेस** → NADPH।',
            },
            {
              english: 'Produces ATP + **NADPH + O₂** (hill reaction).',
        hindi: 'ATP + **NADPH + O₂** (पहाड़ी प्रतिक्रिया) उत्पन्न करता है।',
            },
            {
              english: 'O₂ comes from **photolysis of H₂O at PS II** (oxygen-evolving complex; Mn²⁺, Cl⁻, Ca²⁺):',
        hindi: 'O₂ PS II** पर H₂O के **फोटोलिसिस (ऑक्सीजन विकसित करने वाला कॉम्प्लेक्स; Mn²⁺, Cl⁻, Ca²⁺) से आता है: **चक्रीय फोटोफॉस्फोराइलेशन** - केवल PS I सक्रिय (PS II निष्क्रिय या तरंग दैर्ध्य > 680 एनएम); इलेक्ट्रॉन Fd → Cyt b₆/f → PC → PS I के माध्यम से पुनर्चक्रित होते हैं।',
            },
          ],
        },
        {
          type: 'equation',
          latex: '2\\,H_2O \\rightarrow 4\\,H^+ + O_2 + 4\\,e^-',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Cyclic photophosphorylation** — only PS I active (PS II inactive or wavelength > 680 nm); electrons recycle through Fd → Cyt b₆/f → PC → PS I.',
        hindi: '**चक्रीय फोटोफॉस्फोराइलेशन** - केवल PS I सक्रिय (PS II निष्क्रिय या तरंग दैर्ध्य > 680 एनएम); इलेक्ट्रॉन Fd → Cyt b₆/f → PC → PS I के माध्यम से पुनर्चक्रित होते हैं।',
            },
            {
              english: '• Produces **only ATP**, no NADPH, no O₂.',
        hindi: '• **केवल ATP** उत्पन्न करता है, कोई NADPH नहीं, कोई O₂ नहीं।',
            },
            {
              english: '• Occurs in **stroma lamellae** (lack PS II + NADP reductase).',
        hindi: '• **स्ट्रोमा लैमेला** (PS II + NADP रिडक्टेस की कमी) में होता है। [[[पीपीएक्स_एसईजी_2]] **केमियोस्मोटिक परिकल्पना (मिशेल)** - एच⁺ थायलाकोइड लुमेन में जमा होता है → प्रोटॉन ग्रेडिएंट → **एफ₀-एफ₁ एटीपी सिंथेज़** → एटीपी के माध्यम से प्रवाहित होता है।',
            },
            {
              english: '**Chemiosmotic hypothesis (Mitchell)** — H⁺ accumulates in thylakoid lumen → proton gradient → flow through **F₀-F₁ ATP synthase** → ATP.',
        hindi: '**केमियोस्मोटिक परिकल्पना (मिशेल)** - H⁺ थायलाकोइड लुमेन में जमा होता है → प्रोटॉन ग्रेडिएंट → **F₀-F₁ ATP सिंथेज़** → ATP के माध्यम से प्रवाहित होता है।',
            },
            {
              english: 'For every 2 e⁻ through Z-scheme: **2 NADPH + 3 ATP** (some say 2) + 1 O₂ released.',
        hindi: 'Z-स्कीम के माध्यम से प्रत्येक 2 ई⁻ के लिए: **2 एनएडीपीएच + 3 एटीपी** (कुछ कहते हैं 2) + 1 ओ₂ जारी।',
            },
          ],
        },
      ],
    },
    {
      id: 'dark-reaction',
      title: {
        english: 'Calvin Cycle (C₃ Pathway)',
        hindi: 'कैल्विन चक्र (C₃ मार्ग)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Discovered by **Melvin Calvin** (Nobel 1961) in *Chlorella* using ¹⁴C.',
        hindi: '**मेल्विन केल्विन** (नोबेल 1961) द्वारा *क्लोरेला* में ¹⁴C का उपयोग करके खोजा गया।',
            },
            {
              english: 'Site = **stroma**. Three phases:',
        hindi: 'साइट = **स्ट्रोमा**। तीन चरण:',
            },
            {
              english: '• **Carboxylation** — CO₂ + RuBP (5C) → 2 × **3-PGA (3C)**. Catalysed by **RuBisCO** (Ribulose bisphosphate carboxylase-oxygenase) — most abundant protein in biosphere.',
        hindi: '• **कार्बोक्सिलेशन** — CO₂ + RuBP (5C) → 2 × **3-PGA (3C)**। **RuBisCO** (राइबुलोज बिस्फोस्फेट कार्बोक्सिलेज-ऑक्सीजिनेज) द्वारा उत्प्रेरित - जीवमंडल में सबसे प्रचुर प्रोटीन। • **कमी** - 3-पीजीए → जी3पी (पीजीएएल) **2 एटीपी + 2 एनएडीपीएच प्रति सीओ₂** का उपयोग करके।',
            },
            {
              english: '• **Reduction** — 3-PGA → G3P (PGAL) using **2 ATP + 2 NADPH per CO₂**.',
        hindi: '• **कमी** - 3-पीजीए → जी3पी (पीजीएएल) **2 एटीपी + 2 एनएडीपीएच प्रति सीओ₂** का उपयोग करके।',
            },
            {
              english: '• **Regeneration** — RuBP regenerated using **1 ATP per CO₂**.',
        hindi: '• **पुनर्जनन** - आरयूबीपी को **1 एटीपी प्रति सीओ₂** का उपयोग करके पुनर्जीवित किया गया।',
            },
            {
              english: 'Net per glucose: **6 CO₂, 18 ATP, 12 NADPH**.',
        hindi: 'प्रति ग्लूकोज शुद्ध: **6 CO₂, 18 ATP, 12 NADPH**।',
            },
          ],
        },
        {
          type: 'equation',
          latex: '6\\,CO_2 + 18\\,ATP + 12\\,NADPH \\rightarrow C_6H_{12}O_6 + 18\\,ADP + 12\\,NADP^+',
        },
      ],
    },
    {
      id: 'c4-cam',
      title: {
        english: 'C₄ Pathway, Photorespiration & CAM',
        hindi: 'C₄ मार्ग, प्रकाश-श्वसन और CAM',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**C₄ plants** — first stable product is **OAA (4C)**; e.g. maize, sugarcane, sorghum, *Amaranthus*.',
        hindi: '**C₄ पौधे** - पहला स्थिर उत्पाद **OAA (4C)** है; जैसे मक्का, गन्ना, ज्वार, *ऐमारैंथस*।',
            },
            {
              english: '• **Kranz anatomy** — bundle-sheath cells around vascular bundle, large + chloroplasts often without grana, no PS II → no O₂ here → no photorespiration.',
        hindi: '• **क्रांज़ एनाटॉमी** - संवहनी बंडल के चारों ओर बंडल-शीथ कोशिकाएं, बड़े + क्लोरोप्लास्ट अक्सर ग्रैना के बिना, कोई पीएस II नहीं → यहां कोई O₂ नहीं → कोई फोटोरेस्पिरेशन नहीं।',
            },
            {
              english: '• **Hatch-Slack pathway** — in mesophyll: PEP + CO₂ → OAA (by **PEP carboxylase, PEPcase**) → malate → transported to bundle sheath → decarboxylated → CO₂ enters Calvin cycle (high CO₂ → no O₂ binding by RuBisCO).',
        hindi: '• **हैच-स्लैक मार्ग** - मेसोफिल में: पीईपी + सीओ₂ → ओएए (**पीईपी कार्बोक्सिलेज, पीईपीकेस** द्वारा) → मैलेट → बंडल शीथ में ले जाया गया → डीकार्बोक्सिलेटेड → सीओ₂ केल्विन चक्र में प्रवेश करता है (उच्च सीओ₂ → रूबिस्को द्वारा कोई ओ₂ बंधन नहीं)।',
            },
            {
              english: '• Higher productivity, tolerate higher T, less water.',
        hindi: '• उच्च उत्पादकता, उच्च टी, कम पानी सहन करता है।',
            },
            {
              english: '**Photorespiration** — at high O₂/low CO₂, RuBisCO acts as oxygenase: RuBP + O₂ → 1 PGA + 1 phosphoglycolate (2C) → glycolate cycle in chloroplast + peroxisome + mitochondria → CO₂ released, no ATP, no sugar. **Wasteful** in C₃; absent in C₄.',
        hindi: '**फोटोरेस्पिरेशन** - उच्च O₂/निम्न CO₂ पर, RuBisCO ऑक्सीजनेज के रूप में कार्य करता है: RuBP + O₂ → 1 PGA + 1 फॉस्फोग्लाइकोलेट (2C) → क्लोरोप्लास्ट में ग्लाइकोलेट चक्र + पेरॉक्सिसोम + माइटोकॉन्ड्रिया → CO₂ जारी, कोई एटीपी नहीं, कोई चीनी नहीं। **अपव्ययी** C₃ में; C₄ में अनुपस्थित।',
            },
            {
              english: '**CAM (Crassulacean Acid Metabolism)** — *Opuntia, Pineapple, Agave, succulents*. Stomata **open at night** → CO₂ fixed as malic acid (stored in vacuole). Day: stomata closed, malate decarboxylated → CO₂ for Calvin cycle. **Adaptation to xeric conditions**.',
        hindi: '**सीएएम (क्रसुलेसियन एसिड मेटाबॉलिज्म)** - *ओपंटिया, अनानास, एगेव, रसीला*। स्टोमेटा **रात में खुलता है** → CO₂ मैलिक एसिड के रूप में स्थिर होता है (रिधान में संग्रहीत)। दिन: रंध्र बंद, मैलेट डिकार्बॉक्सिलेटेड → केल्विन चक्र के लिए CO₂। **जैरिक स्थितियों के लिए अनुकूलन**।',
            },
          ],
        },
      ],
    },
    {
      id: 'factors',
      title: {
        english: 'Factors Affecting Photosynthesis',
        hindi: 'प्रकाश संश्लेषण को प्रभावित करने वाले कारक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Blackman\'s Law of Limiting Factors (1905)** — when several factors affect a process, rate is determined by the factor in least amount.',
        hindi: '**ब्लैकमैन का सीमित कारकों का नियम (1905)** - जब कई कारक एक प्रक्रिया को प्रभावित करते हैं, तो दर कम से कम मात्रा में कारक द्वारा निर्धारित की जाती है।',
            },
            {
              english: '**Light** — saturation at ~10 % full sunlight; high light intensity → photo-oxidation.',
        hindi: '**प्रकाश** - ~10% पूर्ण सूर्य के प्रकाश पर संतृप्ति; उच्च प्रकाश तीव्रता → फोटो-ऑक्सीकरण।',
            },
            {
              english: '**CO₂** — most important limiting factor; current atmospheric ~0.04 %; saturation in C₃ at 0.05 %, in C₄ at 0.01 %.',
        hindi: '**CO₂** - सबसे महत्वपूर्ण सीमित कारक; वर्तमान वायुमंडलीय ~0.04 %; C₃ में संतृप्ति 0.05%, C₄ में 0.01% पर।',
            },
            {
              english: '**Temperature** — C₄ optimum 30–45 °C; C₃ optimum 20–25 °C.',
        hindi: '**तापमान** — C₄ इष्टतम 30-45 डिग्री सेल्सियस; C₃ इष्टतम 20-25 डिग्री सेल्सियस।',
            },
            {
              english: '**Water** — affects stomatal opening + leaf turgor.',
        hindi: '**पानी** - रंध्र के उद्घाटन + पत्ती स्फीति को प्रभावित करता है।',
            },
            {
              english: '**Compensation point** — light intensity at which **rate of photosynthesis = rate of respiration** (no net gas exchange). Higher in C₃ than C₄.',
        hindi: '**मुआवजा बिंदु** - प्रकाश की तीव्रता जिस पर **प्रकाश संश्लेषण = rate of श्वसन** (no net gas exchange) की दर। C₄ की तुलना में C₃ अधिक है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '6\\,CO_2 + 12\\,H_2O \\rightarrow C_6H_{12}O_6 + 6\\,O_2 + 6\\,H_2O',
      desc: {
        english: 'Net photosynthesis',
        hindi: 'शुद्ध प्रकाश संश्लेषण',
      },
    },
    {
      latex: '\\text{O}_2 \\text{ source} = \\text{H}_2\\text{O (PS II photolysis)}',
      desc: {
        english: 'van Niel; Ruben-Kamen ¹⁸O',
        hindi: 'वैन नील; रूबेन-कामेन ¹⁸O',
      },
    },
    {
      latex: '\\text{P700 (PS I), P680 (PS II); Z-scheme} \\rightarrow \\text{NADPH + ATP}',
      desc: {
        english: '',
        hindi: 'पी700 (पीएस I), पी680 (पीएस II); जेड-योजना एनएडीपीएच + एटीपी',
      },
    },
    {
      latex: '\\text{Cyclic: only ATP (PS I, stroma lamellae)}',
      desc: {
        english: '',
        hindi: 'चक्रीय: केवल एटीपी (पीएस I, स्ट्रोमा लैमेला)',
      },
    },
    {
      latex: '\\text{Per glucose (C}_3\\text{): 6 CO}_2 + 18\\,\\text{ATP} + 12\\,\\text{NADPH}',
      desc: {
        english: '',
        hindi: 'प्रति ग्लूकोज (सी 3): 6 सीओ 2 + 18\\, एटीपी + 12\\, एनएडीपीएच',
      },
    },
    {
      latex: '\\text{C}_4\\text{: OAA (4C); PEPcase + Kranz anatomy; no photorespiration}',
      desc: {
        english: '',
        hindi: 'सी 4: ओएए (4सी); पीईपीकेस + क्रैंज़ एनाटॉमी; कोई प्रकाश श्वसन नहीं',
      },
    },
    {
      latex: '\\text{CAM: stomata open at night (xerophytic adaptation)}',
      desc: {
        english: '',
        hindi: 'सीएएम: रात में रंध्र खुले (जेरोफाइटिक अनुकूलन)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'C4',
      problem: {
        english: 'First stable product in C₄ plants is:',
        hindi: 'C₄ पौधों में पहला स्थिर उत्पाद है:',
      },
      answer: {
        english: 'Oxaloacetic acid (OAA, 4C)',
        hindi: 'ऑक्सैलोएसिटिक एसिड (OAA, 4C)',
      },
      solution: {
        english: 'Formed by PEP + CO₂ via PEPcase in mesophyll.',
        hindi: 'मेसोफिल में PEPकेस के माध्यम से PEP + CO₂ द्वारा निर्मित।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Light',
      problem: {
        english: 'Cyclic photophosphorylation produces:',
        hindi: 'चक्रीय फोटोफॉस्फोराइलेशन उत्पन्न करता है:',
      },
      answer: {
        english: 'Only ATP (no NADPH, no O₂)',
        hindi: 'केवल एटीपी (कोई एनएडीपीएच नहीं, कोई ओ₂ नहीं)',
      },
      solution: {
        english: 'Only PS I active; electrons recycle.',
        hindi: 'केवल PS I सक्रिय; इलेक्ट्रॉनों का पुनर्चक्रण होता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Enzyme',
      problem: {
        english: 'Most abundant enzyme in biosphere is:',
        hindi: 'जीवमंडल में सबसे प्रचुर मात्रा में पाया जाने वाला एंजाइम है:',
      },
      answer: {
        english: 'RuBisCO',
        hindi: 'रुबिस्को',
      },
      solution: {
        english: 'Bifunctional — carboxylase + oxygenase.',
        hindi: 'द्विकार्यात्मक - कार्बोक्सिलेज + ऑक्सीजनेज़।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Source',
      problem: {
        english: 'O₂ released in photosynthesis comes from:',
        hindi: 'प्रकाश संश्लेषण में जारी O₂ से आता है:',
      },
      answer: {
        english: 'Water (H₂O)',
        hindi: 'पानी (H₂O)',
      },
      solution: {
        english: 'Confirmed by Ruben & Kamen using ¹⁸O.',
        hindi: 'रुबेन और कामेन द्वारा ¹⁸O का उपयोग करके पुष्टि की गई।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Anatomy',
      problem: {
        english: 'Kranz anatomy is characteristic of:',
        hindi: 'क्रैंज़ शरीर रचना की विशेषता है:',
      },
      answer: {
        english: 'C₄ plants',
        hindi: 'C₄ पौधे',
      },
      solution: {
        english: 'Bundle sheath cells with chloroplasts lacking grana.',
        hindi: 'ग्रैना की कमी वाले क्लोरोप्लास्ट के साथ बंडल शीथ कोशिकाएं।',
      },
    },
  ],
  next: {
    slug: 'respiration-plants',
    title: 'Respiration in Plants',
  },
};

export default chapter;
